import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { setToken } from '../api';
import { useSearchParams } from 'react-router-dom';

export default function Signup() {
  const [email, setEmail]               = useState('');
  const [verificationCode, setCode]     = useState('');
  const [password, setPassword]         = useState('');
  const [searchParams] = useSearchParams();
  const initialRef   = searchParams.get('ref') || '';
  const [referral, setReferral]         = useState(initialRef);
  const [error, setError]               = useState('');
  const [loadingSend, setLoadingSend]   = useState(false);
  const [loadingSignup, setLoadingSignup] = useState(false);
  const [codeSent, setCodeSent]         = useState(false);
  const [showPopup, setShowPopup]       = useState(false);
  const navigate                        = useNavigate();

  // Step 1: Send verification code to email
  const handleSendCode = async e => {
    e.preventDefault();
    setError('');
    if (!email) {
      setError('Please enter a valid email');
      return;
    }

    try {
      setLoadingSend(true);
      await api.post('/auth/send-verification', { email });
      setCodeSent(true);
      setShowPopup(true);
      // auto-dismiss popup after 3s
      setTimeout(() => setShowPopup(false), 3000);
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoadingSend(false);
    }
  };

  // Step 2: Complete signup with code
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!verificationCode) {
      setError('Please enter the verification code');
      return;
    }

    setLoadingSignup(true);
    try {
      const { data } = await api.post('/auth/signup', {
        email,
        password,
        referral: referral || undefined,
        emailcode: verificationCode  // match server-side field for verification
      });

      // store token & email
      localStorage.setItem('token', data.token);
      localStorage.setItem('email', data.user.email);
      setToken(data.token);

      // redirect to profile
      navigate('/profile', { replace: true });
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.response?.data?.message || 'Signup failed');
    } finally {
      setLoadingSignup(false);
    }
  };

  return (
    <div className="relative max-w-md mx-auto -mt-12 p-6 bg-gray-800 rounded-lg shadow-lg font-mono">
      {/* Popup Notification */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pointer-events-none">
          <div className="mt-16 px-6 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white rounded-2xl shadow-xl backdrop-blur-md animate-fade-in-out pointer-events-auto">
            ✅ Verification code sent! Check your email (and spam folder).
          </div>
        </div>
      )}

      <h2 className="text-2xl font-bold mb-4 text-white text-center">Create Account</h2>
      {error && <div className="mb-4 text-red-400 text-sm">{error}</div>}

      {/* Step 1: Send Verification Code */}
      {!codeSent ? (
        <form onSubmit={handleSendCode} className="space-y-4">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <button
            type="submit"
            disabled={loadingSend}
            className="w-full py-2 bg-gradient-to-r from-violet-500 to-pink-500 rounded text-white font-semibold hover:from-violet-600 hover:to-pink-600 transition"
          >
            {loadingSend ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                <span>Sending code...</span>
              </div>
            ) : (
              'Send Verification Code'
            )}
          </button>

        </form>
      ) : (
        /* Step 2: Enter Code & Signup */
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            value={email}
            disabled
            className="w-full px-3 py-2 bg-gray-700 text-gray-400 rounded cursor-not-allowed"
          />
          <input
            type="text"
            placeholder="Verification Code"
            value={verificationCode}
            onChange={e => setCode(e.target.value)}
            required
            className="w-full px-3 py-2 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            className="w-full px-3 py-2 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <input
            type="text"
            placeholder="Referral Code (optional)"
            value={referral}
            onChange={e => setReferral(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <button
            type="submit"
            disabled={loadingSignup}
            className="w-full py-2 bg-gradient-to-r from-violet-500 to-pink-500 rounded text-white font-semibold hover:from-violet-600 hover:to-pink-600 transition"
          >
            {loadingSignup ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                <span>Signing up...</span>
              </div>
            ) : (
              'Sign Up'
            )}
          </button>
        </form>
      )}

      <p className="mt-4 text-sm text-gray-400 text-center">
        Already have an account?{' '}
        <Link to="/login" className="text-violet-400 hover:text-violet-200">
          Log In
        </Link>
      </p>
    </div>
  );
}
